import React, { forwardRef, useImperativeHandle, useMemo } from "react";
import { Typography, Table, Col } from "antd";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import Comment from "components/Comments";
import CiResultDisplay from "./CiResultDisplay";
import { resolveMeetingDataProp } from "utils/dpatAssessmentStartData";
import { documentsColumn } from "utils/tableColums";
import { renderDpatEvidenceLink } from "utils/renderDpatEvidenceLink";

const { Title } = Typography;

const MEETING_LINK_KEYS = ["docs", "attendance", "recommendation"];

const LINK_LABELS = {
  docs: "View Minutes",
  attendance: "View Attendance",
  recommendation: "View Allowance",
};

function formatOptionalText(value) {
  if (value == null || value === "" || value === "N/A") {
    return "";
  }
  return value;
}

const EntityTenderCommitteeMeeting = forwardRef(({
  data,
  year,
  columns,
  districtId,
  hideComment,
  savedAssessmentData,
  isAssessmentStarted,
}, ref) => {
  const displayData = resolveMeetingDataProp({
    isAssessmentStarted,
    savedAssessmentData,
    liveData: data,
  });

  const meetingRows = displayData?.data ?? [];
  const documentLinks = displayData?.links ?? data?.links ?? [];

  const meetingTableColumns = useMemo(
    () => (columns ?? []).map((col) => {
      if (MEETING_LINK_KEYS.includes(col.dataIndex)) {
        return {
          ...col,
          render: (value) => renderDpatEvidenceLink(
            value,
            LINK_LABELS[col.dataIndex] ?? "View Document",
          ),
        };
      }

      if (["invitationDate", "meetingDate", "minutes", "invitationLetterReference"].includes(col.dataIndex)) {
        return {
          ...col,
          render: (value) => formatOptionalText(value),
        };
      }

      return col;
    }),
    [columns],
  );

  const documentTableColumns = useMemo(
    () => documentsColumn.map((col) =>
      col.dataIndex === "link"
        ? {
            ...col,
            render: (value) => renderDpatEvidenceLink(value, "View Document"),
          }
        : col
    ),
    [],
  );

  const DPAT_SLUG = "c2.0-2.5";

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "CI2",
      area: "Other Statutory Meetings / Requirements",
      data: {
        ...data,
        links: displayData?.links ?? data?.links,
      },
    }),
  }));

  return (
    <Comment
      data={displayData}
      year={year}
      districtId={districtId}
      tableCommentedId={`c2.0-2.5-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
        <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
          />

          <Col align="start">
            <CiResultDisplay slug={DPAT_SLUG} fulfillment={displayData?.fulfillment} />
          </Col>

          <Title level={4} style={{ marginTop: "20px" }}>Evidence of Spatial Planning Committee (SPC) Meeting</Title>
          {meetingRows.length > 0 ? (
            <Table
              columns={meetingTableColumns}
              dataSource={meetingRows}
              pagination={false}
              bordered
              rowKey={(record, index) => record.key || index}
            />
          ) : null}

          <Title level={4} style={{ marginTop: "20px" }}>Evidence of Attached Documents</Title>
          <Table
            columns={documentTableColumns}
            dataSource={documentLinks}
            pagination={false}
            bordered
            rowKey="title"
          />
          {!hideComment && renderCommentFeedbackFooter()}
        </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default EntityTenderCommitteeMeeting;
